import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Icon,
  Text,
  VStack,
  useToast,
  SimpleGrid,
  Box,
} from '@chakra-ui/react'
import { AlertCircleIcon } from '../../assets/icons'
import handleToast from '../HandleToast'
import ModalInput from '../forms/modalForms/modalInput'
import { CustomDateInput } from '../forms/modalForms/customDate'

interface DiscardModalProps {
  show: boolean

  handleClose: () => void
  notRow?: boolean
  handleDelete: () => void

  action: 'disable' | 'delete' | 'restore'
  deactivatedData?: { [key: string]: string }
  setDeactivatedData?: (data: { [key: string]: string }) => void
  selectedRowsCount: number
  isLoading?: boolean
}

const DiscardModal: React.FC<DiscardModalProps> = ({
  show,
  handleClose,
  handleDelete,
  action,
  selectedRowsCount,
  notRow,
  deactivatedData,
  setDeactivatedData,
  isLoading: isLoadingProp,
}) => {
  const { t } = useTranslation()
  const isMultiple = selectedRowsCount > 1
  const actionLabel =
    action === 'disable'
      ? t('common.actions.disable')
      : action === 'restore'
        ? t('common.actions.restore')
        : t('common.actions.delete')
  const itemLabel = t('common.labels.row', { count: selectedRowsCount })
  const toast = useToast()
  const [isLocalLoading, setIsLocalLoading] = React.useState(false)
  const isLoading = isLoadingProp ?? isLocalLoading

  return (
    <Modal isOpen={show} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader p={'12px 36px 12px 16px'}>
          {notRow
            ? t('common.modals.deleteItemTitle')
            : t('common.modals.actionCountTitle', {
              action: actionLabel,
              count: selectedRowsCount,
              item: itemLabel,
            })}
        </ModalHeader>
        <ModalCloseButton
          sx={{
            _focus: { outline: 'none', border: 'none', boxShadow: 'none' },
            _hover: { bg: 'solid.50' },
          }}
        />
        <ModalBody borderTopWidth="1px" borderTop={'solid.25'} display="flex" justifyContent="center">
          <VStack>
            <Icon color="error.100" w="56px" h="56px" viewBox="0 0 24 24" fill="none">
              <AlertCircleIcon />
            </Icon>
            <Text textStyle="l-s" textAlign="center">
              {notRow
                ? t('messages.confirmDelete')
                : t('common.modals.confirmAction', { count: selectedRowsCount, action: actionLabel })}
            </Text>
            {action === 'disable' ? (
              <Text textStyle="l-xs" color="solid.400" textAlign="center">
                {t('common.modals.disableHint')}
              </Text>
            ) : (
              <Text textStyle="l-xs" color="solid.400" textAlign="center">
                {t('common.modals.deleteHint')}
              </Text>
            )}

            {action === 'disable' && (
              <SimpleGrid columns={1} w="100%" mb="20px" gap={4}>
                <ModalInput
                  label={t('workforce.people.deactivation.exitReasonLabel')}
                  placeholder={t('workforce.people.deactivation.exitReasonPlaceholder')}
                  type="text"
                  name="deactive_reason"
                  value={deactivatedData?.reason || ''}
                  onChange={e => setDeactivatedData?.({ ...deactivatedData, reason: e.target.value })}
                />
                <Box width="100%">
                  <Text textStyle="l-xs" color="solid.500" mb={1}>
                    {t('workforce.people.deactivation.lastDayLabel')}
                  </Text>
                  <CustomDateInput
                    onChange={selectedDate =>
                      setDeactivatedData?.({
                        ...deactivatedData,
                        last_day: selectedDate
                          ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`
                          : '',
                      })
                    }
                  />
                </Box>
                <ModalInput
                  label={t('workforce.people.deactivation.exitEvaluationLabel')}
                  placeholder={t('workforce.people.deactivation.exitEvaluationPlaceholder')}
                  type="text"
                  name="status_exit_evaluation"
                  value={deactivatedData?.status_exit_evaluation || ''}
                  onChange={e => setDeactivatedData?.({ ...deactivatedData, status_exit_evaluation: e.target.value })}
                />
                <ModalInput
                  label={t('workforce.people.deactivation.statusAccessITLabel')}
                  placeholder={t('workforce.people.deactivation.statusAccessITPlaceholder')}
                  type="text"
                  name="status_access_it"
                  value={deactivatedData?.status_access_it || ''}
                  onChange={e => setDeactivatedData?.({ ...deactivatedData, status_access_it: e.target.value })}
                />
                <ModalInput
                  label={t('workforce.people.deactivation.statusKeysLabel')}
                  placeholder={t('workforce.people.deactivation.statusKeysPlaceholder')}
                  type="text"
                  name="status_keys"
                  value={deactivatedData?.status_keys || ''}
                  onChange={e => setDeactivatedData?.({ ...deactivatedData, status_keys: e.target.value })}
                />
                <ModalInput
                  label={t('workforce.people.deactivation.statusCompanyDevicesLabel')}
                  placeholder={t('workforce.people.deactivation.statusCompanyDevicesPlaceholder')}
                  type="text"
                  name="status_company_devices"
                  value={deactivatedData?.status_company_devices || ''}
                  onChange={e => setDeactivatedData?.({ ...deactivatedData, status_company_devices: e.target.value })}
                />
              </SimpleGrid>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter height="72px" gap={4} borderTopWidth="1px" borderTop={'solid.25'} p={4}>
          <Button variant="white" onClick={handleClose} w="100%" isDisabled={isLoading}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            variant="delete"
            w="100%"
            isLoading={isLoading}
            onClick={async () => {
              if (isLoadingProp !== undefined) {
                // Parent manages loading state
                handleDelete()
              } else {
                setIsLocalLoading(true)
                try {
                  handleDelete()
                  handleClose()
                } catch (err) {
                  handleToast(toast, false, t('common.states.error'), t('messages.deleteFailed'))
                } finally {
                  setIsLocalLoading(false)
                }
              }
            }}
          >
            {actionLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default DiscardModal
